import { CALCULATOR_PAIR_SLUGS, CALCULATOR_PAIRS } from './calculatorPairs';
import type { ImageRef, LinkItem } from './types';

/*
 * Nav structure from Figma "Menu Desktop" (148:2113) and footer (27:6510).
 * Legacy URLs (Phase 2 / Webflow export) point at their existing paths so
 * links keep working once those pages are merged.
 */

export interface MegaMenuColumn {
  title: string;
  links: LinkItem[];
}

export const primaryProducts: LinkItem[] = [
  { label: 'Accounts', href: '/' },
  { label: 'Send money', href: '/send-money-home' },
  // Unreleased: keep the /cards route available, but hide it from navigation.
  // { label: 'Cards', href: '/cards' },
  { label: 'Rewards', href: '/rewards' },
  { label: 'Invest', href: '/invest' },
  { label: 'Stablecoin', href: '/stablecoin' },
];

export const megaMenuColumns: MegaMenuColumn[] = [
  {
    title: 'Tools',
    links: [
      { label: 'Calculator', href: '/send-money-home' },
      { label: 'Wishlist', href: 'https://wishlist.nsave.com/', external: true },
      { label: 'Compare', href: '/compare' },
      { label: 'Zakat Calculator', href: '/zakat-calculator' },
      { label: 'Perks', href: '/perks' },
      { label: 'Health Insurance', href: '/health-insurance' },
    ],
  },
  // Temporarily hidden until Use cases pages are built. This column feeds both
  // the desktop mega menu (MegaMenu.tsx) and the mobile drawer (MobileNav.tsx),
  // so re-enabling here restores it in both. Do not delete — just uncomment.
  // {
  //   title: 'Usecases',
  //   links: [
  //     { label: 'For your savings', href: '/' },
  //     { label: 'For remote workers', href: '/' },
  //     { label: 'For travelers', href: '/cards' },
  //     { label: 'For freelancers', href: '/' },
  //     { label: 'For entrepreneurs', href: '/business' },
  //   ],
  // },
];

export const headerNav: { label: string; href?: string; hasMenu?: boolean }[] = [
  { label: 'Personal', href: '/', hasMenu: true },
  { label: 'Business', href: '/business' },
  { label: 'Pricing', href: '/pricing' },
];

export interface FooterColumn {
  title: string;
  links: LinkItem[];
}

export const footerColumns: FooterColumn[] = [
  {
    title: 'Products',
    links: [
      { label: 'Accounts', href: '/' },
      { label: 'Send money home', href: '/send-money-home' },
      { label: 'Business', href: '/business' },
      { label: 'Invest', href: '/invest' },
      { label: 'Rewards', href: '/rewards' },
    ],
  },
  {
    title: 'Tools',
    links: [
      { label: 'Currency converter', href: '/send-money-home' },
      { label: 'Compare', href: '/compare' },
      { label: 'Wishlist', href: 'https://wishlist.nsave.com/', external: true },
      { label: 'Zakat calculator', href: '/zakat-calculator' },
      { label: 'Benefits', href: '/perks' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About us', href: '/about' },
      { label: 'Careers', href: '/careers' },
      { label: 'Partnerships', href: '/partnerships' },
      { label: 'Learn', href: '/help' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms & Conditions', href: '/terms-conditions' },
      { label: 'Privacy policy', href: '/privacy-policy' },
      { label: 'Cookie Policy', href: '/terms/cookie' },
    ],
  },
  {
    title: 'Calculators',
    links: CALCULATOR_PAIR_SLUGS.map((slug) => ({
      label: CALCULATOR_PAIRS[slug].label,
      href: `/calculator/${slug}`,
    })),
  },
];

/**
 * Name of the `primaryProducts` group. Shared by the desktop mega menu and the
 * mobile drawer, which build the same list and must announce it identically.
 */
export const productsLabel: string = 'Products';

/**
 * Mobile drawer chrome (MobileNav).
 *
 * The drawer's own labels and its two flat link lists live here rather than in
 * the component, so a translated site picks them up from its own bundle instead
 * of rendering English inside an otherwise localized menu. The remaining category
 * titles come from `megaMenuColumns`.
 */
export const mobileNav: {
  backLabel: string;
  categoriesLabel: string;
  moreLabel: string;
  openMenuLabel: string;
  closeMenuLabel: string;
  /** Top-level entries that navigate straight through, with no submenu. */
  directLinks: LinkItem[];
  /** Flat links listed below the categories. */
  secondaryLinks: LinkItem[];
} = {
  backLabel: 'Back',
  categoriesLabel: 'Categories',
  moreLabel: 'More',
  openMenuLabel: 'Open menu',
  closeMenuLabel: 'Close menu',
  directLinks: [{ label: 'Business', href: '/business' }],
  secondaryLinks: [
    { label: 'Pricing', href: '/pricing' },
    { label: 'Learn', href: '/help' },
    { label: 'About us', href: '/about' },
  ],
};

export const getAppCta: LinkItem = { label: 'Get the app', href: '/get-the-app' };

/** Featured card in the desktop mega menu (Figma "About us" promo). */
export const megaMenuPromo: { href: string; label: string; image: ImageRef } = {
  href: '/about',
  label: 'About us',
  image: { src: '/images/shared/menu-promo-640.webp', alt: 'People using nsave together' },
};
